<script setup>
import { humanReadableTimestamp, humanReadableDuration, parseDuration } from '@/composables/datetime'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref } from 'vue'
import ArtifactPayload from '@/components/artifacts/ArtifactPayload.vue'
import { useArtifactStore } from '@/store/artifacts.ts'
import { humanReadableFilesize } from '@/composables/filesizeHelpers.js'
import { useInstanceStore } from '@/store/backendInstances'
import { writeToClipboard } from '@/composables/clipboard.ts'
import { useToast } from 'vue-toastification'
import GenericCustomDialog from '@/components/GenericCustomDialog.vue'

const props = defineProps({ artifactId: { type: String, required: true } })

const artifactStore = useArtifactStore()
const instanceStore = useInstanceStore()
const toast = useToast()
const { t } = useI18n()

const fullscreenPayload = ref(false)

const artifact = computed(() => artifactStore.getArtifact(props.artifactId))

const artifactTitle = computed(() => {
  return `${t('artifact.type.' + artifact.value.type)}, ${humanReadableTimestamp(artifact.value.timestamp, false)}`
})

const hasDuration = computed(() => {
  if (!artifact.value) return false
  if (!artifact.value.data) return false
  if (artifact.value.data.time) return true
  return !!artifact.value.data.durationSeconds
})

const humanReadableDurationTime = computed(() => {
  if (!hasDuration.value) return null
  let duration
  if (artifact.value.data.time) {
    duration = parseDuration(artifact.value.data.time.duration)
  } else {
    duration = artifact.value.data.durationSeconds * 1000
  }
  return humanReadableDuration(duration)
})

const isDownloaded = computed(() => artifactStore.isPayloadDownloaded(props.artifactId))

const isDownloading = computed(() => artifactStore.isPayloadDownloading(props.artifactId))

const shareUrl = computed(() => {
  return `${window.location.origin}/instance/${instanceStore.selectedInstanceId}/artifact/${props.artifactId}`
})

async function downloadPayload() {
  await artifactStore.loadArtifactPayload(props.artifactId, undefined, 270, true)
}

async function savePayload() {
  await artifactStore.downloadArtifactPayloadAndSaveToFileSystem(props.artifactId)
}

async function downloadAndSavePayload() {
  await savePayload()
}

async function share(url, title = 'BarnBuddy') {
  try {
    return navigator.share({
      url: url,
      title: title,
    })
  } catch {
    const copyResult = await writeToClipboard(url)
    if (copyResult) {
      toast.success(t('toast.copied_to_clipboard'))
    } else {
      toast.error(t('toast.copy_failed'))
    }
  }
}

onMounted(async () => {
  if (!artifact.value) await artifactStore.loadArtifactData(props.artifactId)
})
</script>

<template>
  <v-row
    v-if="artifact"
    no-gutters
  >
    <!-- image -->
    <v-col
      cols="5"
      :class="{ 'cursor-pointer': isDownloaded }"
      @click="() => isDownloaded && (fullscreenPayload = true)"
    >
      <ArtifactPayload
        :artifact-id="artifact.artifactId"
        :hide-controls="true"
        is-mini-artifact
      />
    </v-col>
    <v-col
      cols="7"
      class="pl-4"
    >
      <div class="h-100 d-flex flex-column justify-space-between">
        <div>
          <p class="text-h6 text-truncate">
            {{ t('artifact.type.' + artifact.type) }}
          </p>
          <p class="text-subtitle-2 text-truncate">
            {{ humanReadableTimestamp(artifact.timestamp, false) }}
          </p>
          <p class="text-subtitle-2 text-truncate">
            {{ `${humanReadableDurationTime} · ${artifact.size ? humanReadableFilesize(artifact.size) : artifact.status}` }}
          </p>
        </div>

        <div class="d-flex flex-row align-center ga-2">
          <!-- Download -->
          <v-btn
            v-if="!isDownloaded && (artifact?.status === 'done' || artifact?.status === 'archived')"
            icon
            size="x-small"
            :disabled="isDownloading"
            @click="downloadPayload"
          >
            <v-icon icon="mdi-download" />
            <v-tooltip
              activator="parent"
              location="top"
              scroll-strategy="close"
            >
              {{ $t('general_interface.buttons.download') }}
            </v-tooltip>
          </v-btn>

          <!-- Save -->
          <v-btn
            v-if="isDownloaded"
            icon
            size="x-small"
            @click="savePayload"
          >
            <v-icon icon="mdi-content-save-outline" />
            <v-tooltip
              activator="parent"
              location="top"
              scroll-strategy="close"
            >
              {{ $t('general_interface.buttons.save') }}
            </v-tooltip>
          </v-btn>

          <!-- Download & Save -->
          <v-btn
            v-if="!isDownloaded && (artifact?.status === 'done' || artifact?.status === 'archived')"
            icon
            size="x-small"
            :disabled="isDownloading"
            @click="downloadAndSavePayload"
          >
            <v-icon icon="mdi-content-save-outline" />
            <v-tooltip
              activator="parent"
              location="top"
              scroll-strategy="close"
            >
              {{ $t('general_interface.buttons.download_and_save') }}
            </v-tooltip>
          </v-btn>

          <!-- Fullscreen -->
          <v-btn
            v-if="isDownloaded"
            icon
            size="x-small"
          >
            <v-icon icon="mdi-fullscreen" />
            <v-tooltip
              activator="parent"
              location="top"
              scroll-strategy="close"
            >
              {{ $t('general_interface.buttons.fullscreen') }}
            </v-tooltip>

            <GenericCustomDialog
              v-model="fullscreenPayload"
              activator="parent"
              width="100%"
              height="100dvh"
              :dialog-props="{ fullscreen: true }"
              body-class="flex-fill overflow-auto px-0"
              hide-cancel-button
            >
              <template #headerIcon>
                <v-icon icon="mdi-image-area" />
              </template>
              <template #title>
                {{ artifactTitle }}
              </template>
              <template #body>
                <ArtifactPayload
                  v-if="fullscreenPayload"
                  :artifact-id="artifact.artifactId"
                  full-screen
                />
              </template>
              <template #actions>
                <v-btn
                  v-if="isDownloaded"
                  color="primary"
                  variant="tonal"
                  prepend-icon="mdi-content-save-outline"
                  @click="savePayload"
                >
                  {{ $t('general_interface.buttons.save') }}
                </v-btn>
                <v-btn
                  prepend-icon="mdi-close"
                  color="primary"
                  variant="flat"
                  @click="fullscreenPayload = false"
                >
                  {{ $t('general_interface.buttons.close') }}
                </v-btn>
              </template>
            </GenericCustomDialog>
          </v-btn>
          <v-btn
            icon
            size="x-small"
            @click="share(shareUrl, artifactTitle)"
          >
            <v-icon icon="mdi-share-variant" />
            <v-tooltip
              activator="parent"
              location="top"
              scroll-strategy="close"
            >
              {{ $t('general_interface.buttons.share') }}
            </v-tooltip>
          </v-btn>
        </div>
      </div>
    </v-col>
  </v-row>
</template>
